/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 * Product Content Info messages
 *
 * @author Yakov Gushchin
 * @author Evgeniy Naumov
 * @version 1.24.0
 * @since 1.11.0
 */

import { useEffect, useState } from 'react';

import { Typography } from '@nexign/scc-ui-kit';
import { connect, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';


import { LayoutStyled } from '@components/common/Block/Layout';
import { EColorStatus } from '@modules/app/appConstants';
import { makeSelectTranslationPageProducts } from '@modules/internationalization/intSelectors';
import { IPageProductsTranslation } from '@modules/internationalization/types/pages/products';
import { FeatureNameEnum } from '@modules/role/constants/guard/guardConstants';
import { makeSelectIsFeatureWithVersionEnabled } from '@pages/PageAuth/selectors/authSelectors';
import { IRootReducer } from '@reducers/rootReducer';
import { stringFormatParams } from '@utils/stringFormat';

import InfoBlock from '../../../components/common/InfoBlock/InfoBlock';
import { EProductsTab } from '../tariffsAndServicesConstants';
import {
    makeSelectProductsCategoryIds,
    makeSelectProductsCategorySelected,
    makeSelectProductsFilterRegionVisible,
    makeSelectProductsIsSubsDifferentRegions,
    makeSelectProductsMeta,
    makeSelectProductsRegionIsDisabled,
    makeSelectProductsRegionSelected,
    makeSelectProductsTabSelected,
} from '../tariffsAndServicesSelectors';
import { IProductCategoryIds, IProductsMeta, IProductsRegionItem } from '../tariffsAndServicesTypes';

interface IProductContentInfo {
    changeRegionInSubsMobile: string;
}

export type TProductContentInfo = IProductContentInfo & IConnectedProps;

export const ProductContentInfo = (props: TProductContentInfo) => {
    const {
        categorySelected,
        productsCategoryIds,
        changeRegionInSubsMobile,
        filterRegionVisible,
        productMeta,
        regionIsDisabled,
        regionIsSubsDifferent,
        regionSelected,
        tabSelected,
        translation,
    } = props;

    const [isChangeRegionForPaymentVisible, setChangeRegionForPaymentVisible] = useState<boolean>(false);
    const [isChangeRegionInSubsMobileVisible, setChangeRegionInSubsMobileVisible] = useState<boolean>(false);
    const [isMaxAvailableServicesVisible, setMaxAvailableServicesVisible] = useState<boolean>(false);
    const [isCampaignInfoVisible, setIsCampaignInfoVisible] = useState<boolean>(false);
    const [isMegaPowersInfoVisible, setIsMegaPowersInfoVisible] = useState<boolean>(false);

    const isTariffsCategory = productsCategoryIds.tariffsCartegoryIds.includes(categorySelected);
    const isMegaPowersFeatureVersion4Enabled = useSelector(
        makeSelectIsFeatureWithVersionEnabled(FeatureNameEnum.MEGA_POWERS, 4, false),
    );

    useEffect(() => {
        setChangeRegionForPaymentVisible(
            tabSelected === EProductsTab.AVAILABLE &&
                !!regionSelected?.label &&
                filterRegionVisible &&
                !regionIsDisabled,
        );
    }, [regionIsDisabled, tabSelected, regionSelected, filterRegionVisible]);

    useEffect(() => {
        setChangeRegionInSubsMobileVisible(regionIsDisabled && regionIsSubsDifferent);
    }, [regionIsDisabled, regionIsSubsDifferent]);

    useEffect(() => {
        setMaxAvailableServicesVisible(productMeta.serviceMaxSelectIsSingle && !isTariffsCategory);
    }, [isTariffsCategory, productMeta]);

    useEffect(() => {
        setMaxAvailableServicesVisible(productMeta.serviceMaxSelectIsSingle && !isTariffsCategory);
    }, [isTariffsCategory, productMeta]);

    useEffect(() => {
        setIsMegaPowersInfoVisible(productMeta.megaPowersCategoryId === categorySelected);
    }, [categorySelected, productMeta.megaPowersCategoryId]);

    useEffect(() => {
        setIsCampaignInfoVisible(
            productMeta.isCampaignInfoVisible &&
                (categorySelected === productMeta.campaignServicesCategoryId ||
                    categorySelected === productMeta.campaignTariffsCategoryId),
        );
    }, [categorySelected, productMeta]);

    return (
        <>
            {
                // Инфо сообщение, если пользователю доступно для выбора ПП несколько регионов
                // (для каждого региона свои цены)
                isChangeRegionForPaymentVisible && (
                    <LayoutStyled className="mt-3 change-region-payment">
                        <InfoBlock colorStatus={EColorStatus.SUCCESS}>
                            {stringFormatParams(translation.filters.regionSelectedInfo, {
                                name: regionSelected.label,
                            })}
                        </InfoBlock>
                    </LayoutStyled>
                )
            }
            {
                // Показаны персональные предложения для выбранного вами абонента
                isCampaignInfoVisible && (
                    <LayoutStyled className="mt-3 change-region-subs">
                        <InfoBlock colorStatus={EColorStatus.SUCCESS}>{translation.campaignInfo}</InfoBlock>
                    </LayoutStyled>
                )
            }
            {
                // Инфо сообщение, если пришли с МС и у пользователя в корзине абоненты из разных регионов
                isChangeRegionInSubsMobileVisible && !isCampaignInfoVisible && (
                    <LayoutStyled className="mt-3 change-region-subs">
                        <InfoBlock colorStatus={EColorStatus.SUCCESS}>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: stringFormatParams(changeRegionInSubsMobile, {
                                        region: regionSelected.label,
                                    }),
                                }}
                            />
                        </InfoBlock>
                    </LayoutStyled>
                )
            }
            {
                // Инфо сообщение, об ограничении выбора услуг.
                // Если пользовател пришел с МС и у него несколько абонентов в корзине
                isMaxAvailableServicesVisible && (
                    <LayoutStyled className="mt-3 max-available-services">
                        <InfoBlock colorStatus={EColorStatus.WARNING}>{translation.maxAvailableServicesInfo}</InfoBlock>
                    </LayoutStyled>
                )
            }
            {
                // Инфо сообщение для мегасильных услуг.
                !isMegaPowersFeatureVersion4Enabled
                && isMegaPowersInfoVisible
                && (translation.megaPowersServicesInfo?.length > 0) && (
                    <LayoutStyled className="mt-3 max-available-services">{
                        <InfoBlock colorStatus={EColorStatus.WARNING}>
                            {translation.megaPowersServicesInfo}
                        </InfoBlock>}
                    </LayoutStyled>
                )
            }
            {
                // Инфо сообщение для мегасильных услуг для 4 версии рычага.
                isMegaPowersFeatureVersion4Enabled
                && isMegaPowersInfoVisible
                && (translation.megaPowersServicesInfoV2?.length > 0) && (
                    <LayoutStyled className="mt-3 max-available-services">{
                        <InfoBlock colorStatus={EColorStatus.WARNING}>
                            <Typography isDangerousInnerHTML>{translation.megaPowersServicesInfoV2}</Typography>
                        </InfoBlock>}
                    </LayoutStyled>
                )
            }
        </>
    );
};

interface IConnectedProps {
    categorySelected: number;
    productsCategoryIds: IProductCategoryIds;
    filterRegionVisible: boolean;
    productMeta: IProductsMeta;
    regionIsDisabled: boolean;
    regionIsSubsDifferent: boolean;
    regionSelected: IProductsRegionItem;
    tabSelected: EProductsTab;
    translation: IPageProductsTranslation;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    categorySelected: makeSelectProductsCategorySelected(),
    productsCategoryIds: makeSelectProductsCategoryIds(),
    filterRegionVisible: makeSelectProductsFilterRegionVisible(),
    productMeta: makeSelectProductsMeta(),
    regionIsDisabled: makeSelectProductsRegionIsDisabled(),
    regionIsSubsDifferent: makeSelectProductsIsSubsDifferentRegions(),
    regionSelected: makeSelectProductsRegionSelected(),
    tabSelected: makeSelectProductsTabSelected(),
    translation: makeSelectTranslationPageProducts(),
});

export default connect(mapStateToProps)(ProductContentInfo);
